import React, { useState, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Gear, 
  CreditCard, 
  Truck, 
  FileText, 
  Globe,
  Eye,
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { currentStore, updateStore, updateStoreSettings, publishStore, fetchStores } = useStore();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'general';
  const [loading, setLoading] = useState(false);
  const [storeData, setStoreData] = useState({
    name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });
  const [settings, setSettings] = useState({
    stripe_public_key: '',
    stripe_secret_key: '',
    paypal_client_id: '',
    paypal_secret: '',
    enable_payment_4x: false,
    enable_cod: false,
    enable_pickup: false,
    enable_paypal: false,
    enable_bank_transfer: false,
    mondial_relay_brand: '',
    enable_mondial_relay: false,
    cgv_content: '',
    legal_mentions: '',
    privacy_policy: '',
    meta_title: '',
    meta_description: '',
    google_analytics_id: '',
    google_review_url: '',
  });

  useEffect(() => {
    if (currentStore) {
      setStoreData({
        name: currentStore.name || '',
        description: currentStore.description || '',
        contact_email: currentStore.contact_email || '',
        contact_phone: currentStore.contact_phone || '',
        address: currentStore.address || ''
      });
      // Fetch full store settings (list_stores API only returns partial data)
      axios.get(`${API_URL}/api/stores/${currentStore.id}`, { withCredentials: true })
        .then(resp => {
          if (resp.data?.settings) {
            setSettings(prev => ({ ...prev, ...resp.data.settings }));
          }
        })
        .catch(() => {});
    }
  }, [currentStore]);

  const handleSaveGeneral = async () => {
    setLoading(true);
    try {
      await updateStore(currentStore.id, storeData);
      toast.success('Informations mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await updateStoreSettings(currentStore.id, settings);
      toast.success('Paramètres mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    try {
      await publishStore(currentStore.id);
      toast.success('Boutique publiée !');
      fetchStores();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  if (!currentStore) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Sélectionnez une boutique pour accéder aux paramètres</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight">Paramètres</h1>
          <p className="text-slate-500 mt-1">Configurez votre boutique {currentStore.name}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open(`/store/${currentStore.slug}`, '_blank')}>
            <Eye className="mr-2" size={18} />
            Aperçu
          </Button>
          {!currentStore.is_published && (
            <Button onClick={handlePublish} className="bg-emerald-600 hover:bg-emerald-700" data-testid="publish-btn">
              <Globe className="mr-2" size={18} />
              Publier
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue={initialTab} className="space-y-6">
        <TabsList className="bg-white border">
          <TabsTrigger value="general" data-testid="tab-general">
            <Gear className="mr-2" size={16} />
            Général
          </TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal">
            <FileText className="mr-2" size={16} />
            Pages légales
          </TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">
            <Globe className="mr-2" size={16} />
            SEO
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
              <CardDescription>Les informations de base de votre boutique</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nom de la boutique</Label>
                  <Input
                    value={storeData.name}
                    onChange={(e) => setStoreData(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="settings-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email de contact</Label>
                  <Input
                    type="email"
                    value={storeData.contact_email}
                    onChange={(e) => setStoreData(prev => ({ ...prev, contact_email: e.target.value }))}
                    data-testid="settings-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={storeData.contact_phone}
                    onChange={(e) => setStoreData(prev => ({ ...prev, contact_phone: e.target.value }))}
                    data-testid="settings-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    value={storeData.address}
                    onChange={(e) => setStoreData(prev => ({ ...prev, address: e.target.value }))}
                    data-testid="settings-address"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={storeData.description}
                  onChange={(e) => setStoreData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  data-testid="settings-description"
                />
              </div>
              <Button onClick={handleSaveGeneral} disabled={loading} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-general">
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle>Configuration des paiements</CardTitle>
              <CardDescription>Configurez vos moyens de paiement</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Stripe */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#635BFF] rounded flex items-center justify-center">
                    <CreditCard size={18} className="text-white" />
                  </div>
                  Stripe
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-10">
                  <div className="space-y-2">
                    <Label>Clé publique</Label>
                    <Input
                      placeholder="pk_live_..."
                      value={settings.stripe_public_key}
                      onChange={(e) => setSettings(prev => ({ ...prev, stripe_public_key: e.target.value }))}
                      data-testid="stripe-public"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Clé secrète</Label>
                    <Input
                      type="password"
                      placeholder="sk_live_..."
                      value={settings.stripe_secret_key}
                      onChange={(e) => setSettings(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                      data-testid="stripe-secret"
                    />
                  </div>
                </div>
              </div>

              {/* PayPal */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#003087] rounded flex items-center justify-center">
                      <span className="text-white text-xs font-bold">PP</span>
                    </div>
                    PayPal
                  </h3>
                  <Switch
                    checked={settings.enable_paypal}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_paypal: checked }))}
                    data-testid="enable-paypal"
                  />
                </div>
                {settings.enable_paypal && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-10">
                    <div className="space-y-2">
                      <Label>Client ID</Label>
                      <Input
                        placeholder="AX..."
                        value={settings.paypal_client_id}
                        onChange={(e) => setSettings(prev => ({ ...prev, paypal_client_id: e.target.value }))}
                        data-testid="paypal-client"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Secret</Label>
                      <Input
                        type="password"
                        placeholder="EL..."
                        value={settings.paypal_secret}
                        onChange={(e) => setSettings(prev => ({ ...prev, paypal_secret: e.target.value }))}
                        data-testid="paypal-secret"
                      />
                    </div>
                    <div className="col-span-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-[12px] text-blue-700">
                        Obtenez vos credentials sur <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="underline font-semibold">developer.paypal.com</a>.
                        Mode sandbox actif par defaut.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Paiement en 4x */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100">
                  <div>
                    <p className="font-semibold text-indigo-900">Paiement en 4x sans frais</p>
                    <p className="text-sm text-indigo-600">Afficher le paiement en 4x sur les pages produit et panier (via Stripe/PayPal)</p>
                  </div>
                  <Switch
                    checked={settings.enable_payment_4x}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_payment_4x: checked }))}
                    data-testid="enable-4x"
                  />
                </div>
              </div>

              {/* Other options */}
              <div className="space-y-4">
                <h3 className="font-semibold">Autres options</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                    <div>
                      <p className="font-medium">Paiement à la livraison</p>
                      <p className="text-sm text-slate-500">Permettre le paiement en espèces à la livraison</p>
                    </div>
                    <Switch
                      checked={settings.enable_cod}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_cod: checked }))}
                      data-testid="enable-cod"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                    <div>
                      <p className="font-medium">Paiement sur place</p>
                      <p className="text-sm text-slate-500">Pour le retrait en magasin</p>
                    </div>
                    <Switch
                      checked={settings.enable_pickup}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_pickup: checked }))}
                      data-testid="enable-pickup"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                    <div>
                      <p className="font-medium">Virement bancaire</p>
                      <p className="text-sm text-slate-500">Permettre le paiement par virement SEPA</p>
                    </div>
                    <Switch
                      checked={settings.enable_bank_transfer}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_bank_transfer: checked }))}
                      data-testid="enable-bank-transfer"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveSettings} disabled={loading} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-payments">
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shipping Tab */}
        <TabsContent value="shipping">
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle>Configuration de l'expédition</CardTitle>
              <CardDescription>Configurez vos modes de livraison</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Mondial Relay */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-600 rounded flex items-center justify-center">
                      <Truck size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">Mondial Relay</p>
                      <p className="text-sm text-slate-500">Livraison en point relais</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.enable_mondial_relay}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_mondial_relay: checked }))}
                    data-testid="enable-mondial-relay"
                  />
                </div>
                {settings.enable_mondial_relay && (
                  <div className="pl-14 space-y-2">
                    <Label>Code client Mondial Relay</Label>
                    <Input
                      placeholder="BDTEST  "
                      value={settings.mondial_relay_brand}
                      onChange={(e) => setSettings(prev => ({ ...prev, mondial_relay_brand: e.target.value }))}
                      data-testid="mondial-relay-brand"
                    />
                    <p className="text-sm text-slate-500">Utilisez "BDTEST  " (avec espaces) pour les tests</p>
                  </div>
                )}
              </div>

              <Button onClick={handleSaveSettings} disabled={loading} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-shipping">
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Legal Tab */}
        <TabsContent value="legal">
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle>Pages légales</CardTitle>
              <CardDescription>Contenu de vos pages légales obligatoires</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Conditions Générales de Vente (CGV)</Label>
                <Textarea
                  value={settings.cgv_content}
                  onChange={(e) => setSettings(prev => ({ ...prev, cgv_content: e.target.value }))}
                  rows={8}
                  placeholder="Rédigez vos CGV..."
                  data-testid="cgv-content"
                />
              </div>
              <div className="space-y-2">
                <Label>Mentions légales</Label>
                <Textarea
                  value={settings.legal_mentions}
                  onChange={(e) => setSettings(prev => ({ ...prev, legal_mentions: e.target.value }))}
                  rows={8}
                  placeholder="Rédigez vos mentions légales..."
                  data-testid="legal-mentions"
                />
              </div>
              <div className="space-y-2">
                <Label>Politique de confidentialité</Label>
                <Textarea
                  value={settings.privacy_policy}
                  onChange={(e) => setSettings(prev => ({ ...prev, privacy_policy: e.target.value }))}
                  rows={8}
                  placeholder="Rédigez votre politique de confidentialité..."
                  data-testid="privacy-policy"
                />
              </div>
              <Button onClick={handleSaveSettings} disabled={loading} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-legal">
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo">
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle>Référencement SEO</CardTitle>
              <CardDescription>Optimisez la visibilité de votre boutique sur les moteurs de recherche</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Titre de la page (meta title)</Label>
                <Input
                  value={settings.meta_title}
                  onChange={(e) => setSettings(prev => ({ ...prev, meta_title: e.target.value }))}
                  placeholder="Ma Boutique - Les meilleurs produits"
                  data-testid="meta-title"
                />
                <p className="text-sm text-slate-500">{settings.meta_title?.length || 0}/60 caractères recommandés</p>
              </div>
              <div className="space-y-2">
                <Label>Description (meta description)</Label>
                <Textarea
                  value={settings.meta_description}
                  onChange={(e) => setSettings(prev => ({ ...prev, meta_description: e.target.value }))}
                  rows={3}
                  placeholder="Découvrez notre sélection de produits..."
                  data-testid="meta-description"
                />
                <p className="text-sm text-slate-500">{settings.meta_description?.length || 0}/160 caractères recommandés</p>
              </div>
              <div className="space-y-2">
                <Label>ID Google Analytics</Label>
                <Input
                  value={settings.google_analytics_id}
                  onChange={(e) => setSettings(prev => ({ ...prev, google_analytics_id: e.target.value }))}
                  placeholder="UA-XXXXXXXXX-X ou G-XXXXXXXXXX"
                  data-testid="ga-id"
                />
              </div>

              <div className="space-y-2">
                <Label>URL Avis Google</Label>
                <Input
                  value={settings.google_review_url || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, google_review_url: e.target.value }))}
                  placeholder="https://g.page/r/XXXXXXX/review"
                  data-testid="google-review-url"
                />
                <p className="text-sm text-slate-500">Lien direct pour deposer un avis sur Google Maps. Vos clients seront invites a laisser un avis apres leur commande.</p>
              </div>
              
              {/* Preview */}
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-sm text-slate-500 mb-2">Aperçu Google</p>
                <div>
                  <p className="text-[#1a0dab] text-lg hover:underline cursor-pointer">
                    {settings.meta_title || 'Titre de votre boutique'}
                  </p>
                  <p className="text-[#006621] text-sm">shopizy.app/{currentStore.slug}</p>
                  <p className="text-sm text-slate-600">
                    {settings.meta_description || 'Description de votre boutique qui apparaîtra dans les résultats de recherche.'}
                  </p>
                </div>
              </div>

              {/* Google Shopping / Merchant Center & SEO Feeds */}
              <div className="p-4 rounded-lg border border-slate-200 space-y-4">
                <div>
                  <p className="font-semibold text-slate-900">Flux SEO & Google Shopping</p>
                  <p className="text-sm text-slate-500">Soumettez ces URLs dans Google Search Console et Google Merchant Center pour booster votre référencement.</p>
                </div>
                {[
                  { label: 'Sitemap XML', path: '/api/sitemap.xml', testid: 'copy-sitemap', desc: 'À ajouter dans Google Search Console.' },
                  { label: 'Robots.txt', path: '/api/robots.txt', testid: 'copy-robots', desc: 'Indique aux moteurs ce qu\'ils peuvent explorer.' },
                  { label: 'Flux Google Merchant Center', path: '/api/merchant-feed.xml', testid: 'copy-merchant', desc: 'À ajouter dans Merchant Center → Produits → Flux pour Google Shopping (gratuit).' },
                ].map((item) => {
                  const fullUrl = `${window.location.origin}${item.path}`;
                  return (
                    <div key={item.path} className="flex flex-col gap-2 p-3 bg-slate-50 rounded-md">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="font-medium">{item.label}</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid={item.testid}
                          onClick={() => {
                            navigator.clipboard.writeText(fullUrl);
                            toast.success(`${item.label} copié !`);
                          }}
                        >
                          Copier l'URL
                        </Button>
                      </div>
                      <code className="text-xs text-slate-700 break-all">{fullUrl}</code>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  );
                })}
                <a
                  href="https://merchants.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-[#0052FF] hover:underline"
                  data-testid="link-merchant-center"
                >
                  → Ouvrir Google Merchant Center
                </a>
              </div>

              <Button onClick={handleSaveSettings} disabled={loading} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-seo">
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
